import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Skeleton,
} from '@mui/material';
import {
  Category as TypeIcon,
  Update as UpdateIcon,
  WorkOutline as CasesIcon,
  PeopleOutline as ContactsIcon,
  TrendingUp as ActivitiesIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { Customer, CustomerType } from 'containers/pages/customers/types';
import CustomerFormUnified from '../../customerFormUnified/CustomerFormUnified';

interface GeneralInfoTabProps {
  customer: Customer;
  customerTypes: CustomerType[];
  onSave: (formData: Record<string, unknown>, isEdit: boolean) => Promise<void>;
  onSaveSuccess: () => void;
}

interface InfoRowProps {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}

function InfoRow({ icon, label, value }: InfoRowProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        py: 1.5,
        px: 2,
        '&:not(:last-child)': {
          borderBottom: 1,
          borderColor: 'divider',
        },
      }}
    >
      <Box sx={{ color: 'primary.main', display: 'flex', flexShrink: 0 }}>
        {icon}
      </Box>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ minWidth: 100, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.05em' }}
      >
        {label}
      </Typography>
      <Box sx={{ flex: 1, textAlign: 'right' }}>
        {typeof value === 'string' || typeof value === 'number' ? (
          <Typography variant="body2" fontWeight={500}>
            {value}
          </Typography>
        ) : (
          value
        )}
      </Box>
    </Box>
  );
}

export function GeneralInfoTabSkeleton() {
  return (
    <Box sx={{ p: 2 }}>
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        {[...Array(5)].map((_, i) => (
          <Box
            key={i}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              py: 1.5,
              px: 2,
              '&:not(:last-child)': { borderBottom: 1, borderColor: 'divider' },
            }}
          >
            <Skeleton variant="circular" width={24} height={24} />
            <Skeleton width={80} height={20} />
            <Box sx={{ flex: 1 }} />
            <Skeleton width={60} height={20} />
          </Box>
        ))}
      </Paper>
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Skeleton width={100} height={36} />
      </Box>
    </Box>
  );
}

export default function GeneralInfoTab({
  customer,
  customerTypes,
  onSave,
  onSaveSuccess,
}: GeneralInfoTabProps) {
  const [editing, setEditing] = useState(false);

  const formattedDate = customer.UpdatedOn
    ? new Date(customer.UpdatedOn).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : 'N/A';

  const handleSave = async (formData: Record<string, unknown>, isEdit: boolean) => {
    await onSave(formData, isEdit);
    setEditing(false);
    onSaveSuccess();
  };

  if (editing) {
    return (
      <Box sx={{ p: 2 }}>
        <CustomerFormUnified
          mode="edit"
          customerId={customer.Id}
          customerTypes={customerTypes}
          onSave={handleSave}
          onClose={() => setEditing(false)}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography
        variant="overline"
        sx={{ fontWeight: 700, letterSpacing: '0.1em', color: 'text.secondary', mb: 1, display: 'block' }}
      >
        Activity Overview
      </Typography>

      <Paper
        variant="outlined"
        sx={{
          overflow: 'hidden',
          borderRadius: 2,
        }}
      >
        <InfoRow
          icon={<TypeIcon fontSize="small" />}
          label="Type"
          value={
            <Chip
              label={customer.TypeTitle || 'Unassigned'}
              size="small"
              sx={{
                fontWeight: 600,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                fontSize: '0.75rem',
              }}
            />
          }
        />
        <InfoRow
          icon={<UpdateIcon fontSize="small" />}
          label="Last Update"
          value={formattedDate}
        />
        <InfoRow
          icon={<CasesIcon fontSize="small" />}
          label="Cases"
          value={customer.NumberOfCases}
        />
        <InfoRow
          icon={<ContactsIcon fontSize="small" />}
          label="Contacts"
          value={customer.NumberOfContacts}
        />
        <InfoRow
          icon={<ActivitiesIcon fontSize="small" />}
          label="Activities"
          value={customer.NumberOfActivities}
        />
      </Paper>

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={() => setEditing(true)}
          aria-label="Edit customer"
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Edit Customer
        </Button>
      </Box>
    </Box>
  );
}
